/**
 * Admin Invites Screen
 * 
 * Invitation management for workspace admins:
 * - Create new invitations with role assignment
 * - View pending/used invitations
 * - Email invitations (and resend the email) when an address is given
 * - Copy invite links
 * - Revoke pending invitations
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Mail,
  Link2,
  Copy,
  Check,
  XCircle,
  RefreshCw,
  Clock,
  UserPlus,
  Shield,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import {
  createInvitation,
  listInvitations,
  resetInvitation,
  getInviteUrl,
  sendInvitationEmail,
  inviteEmailFeedback,
  type InvitationWithDetails,
  type CreateInvitationParams,
} from '../../../lib/invitationService';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { Callout } from '@/components/ds';
import { ReverseActionButton } from '../../../components/reversals/ReverseActionButton';
import { revokeWorkspaceInvitation } from '../../../lib/reversals/reversalsService';

// ============================================================================
// Types
// ============================================================================

interface OrgUnit {
  id: string;
  name: string;
  displayName: string;
  type: string;
  parentId: string | null;
  depth: number;
}

/** Role as returned from the roles table (assignable in invitations) */
export interface AssignableRole {
  id: string;
  name: string;
  label: string;
  description: string;
}

// ============================================================================
// Main Component
// ============================================================================

export default function AdminInvites() {
  const { 
    currentWorkspaceId, 
    currentTenantId, 
    getCurrentWorkspace,
    workspaces,
    fetchWorkspaces,
  } = useTenantStore();
  const { userId, currentRole } = useUserStore();
  const currentWorkspace = getCurrentWorkspace();
  const isPlatformAdmin = currentRole === 'platform-admin' || currentRole === 'system-admin';
  const addNotification = useUIStore((s) => s.addNotification);

  // Data state
  const [invitations, setInvitations] = useState<InvitationWithDetails[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [assignableRoles, setAssignableRoles] = useState<AssignableRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  /** The invitation whose email is being resent right now. */
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'used' | 'expired'>('all');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  /** The invitation the admin just created — highlighted in the list after "Done". */
  const [highlightedInviteId, setHighlightedInviteId] = useState<string | null>(null);

  // For platform admins, use selected workspace or current workspace
  const effectiveWorkspaceId = selectedWorkspaceId || currentWorkspaceId;

  // Fetch workspaces for the current tenant (for platform admin workspace selector)
  useEffect(() => {
    if (isPlatformAdmin && currentTenantId) {
      fetchWorkspaces(currentTenantId);
    }
  }, [isPlatformAdmin, currentTenantId, fetchWorkspaces]);

  // Fetch org units and assignable roles for the current workspace
  useEffect(() => {
    if (!effectiveWorkspaceId) return;

    async function fetchOrgUnits() {
      try {
        const { getSafeSupabaseClient } = await import('../../../lib/supabaseSync');
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;

        const { data, error: fetchError } = await supabase
          .from('org_units')
          .select('id, name, display_name, type, parent_id, depth')
          .eq('workspace_id', effectiveWorkspaceId)
          .order('depth', { ascending: true })
          .order('name', { ascending: true });

        if (!fetchError && data) {
          setOrgUnits(data.map(row => ({
            id: row.id,
            name: row.name,
            displayName: row.display_name,
            type: row.type,
            parentId: row.parent_id,
            depth: row.depth,
          })));
        }
      } catch (err) {
        console.error('[AdminInvites] Error fetching org units:', err);
      }
    }

    async function fetchRoles() {
      try {
        const supabase = getSupabaseClient();
        const { data, error: rolesError } = await supabase
          .from('roles')
          .select('id, name, display_name, description')
          .or(`is_system.eq.true,workspace_id.eq.${effectiveWorkspaceId},workspace_id.is.null`)
          .order('name');

        if (!rolesError && data) {
          setAssignableRoles(data.map(r => ({
            id: r.id,
            name: r.name,
            label: r.display_name || r.name,
            description: r.description || '',
          })));
        }
      } catch (err) {
        console.error('[AdminInvites] Error fetching roles:', err);
      }
    }

    fetchOrgUnits();
    fetchRoles();
  }, [effectiveWorkspaceId]);

  // ========================================================================
  // Data Fetching
  // ========================================================================

  const fetchInvitations = useCallback(async () => {
    if (!effectiveWorkspaceId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await listInvitations(effectiveWorkspaceId);
      setInvitations(data);
    } catch (err) {
      console.error('[AdminInvites] Error fetching invitations:', err);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [effectiveWorkspaceId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // ========================================================================
  // Actions
  // ========================================================================

  const handleCopyLink = async (invitation: InvitationWithDetails) => {
    const url = getInviteUrl(invitation.token);
    await navigator.clipboard.writeText(url);
    setCopiedId(invitation.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleResendEmail = async (invitation: InvitationWithDetails) => {
    if (!invitation.email) return;
    setResendingId(invitation.id);
    const result = await sendInvitationEmail(invitation.id);
    setResendingId(null);
    const feedback = inviteEmailFeedback(invitation.email, result);
    addNotification(
      feedback.sent
        ? { type: 'success', title: 'Email sent', message: feedback.message, duration: 4000 }
        : { type: 'warning', title: 'Email not sent', message: feedback.message, duration: 8000 },
    );
  };

  const handleReset = async (invitationId: string) => {
    if (!confirm('Reset this invitation so it can be used again?')) return;

    const result = await resetInvitation(invitationId);
    if (result.success) {
      // Update local state to reflect the reset
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId 
          ? { ...inv, usedAt: null, usedBy: null } 
          : inv
      ));
    } else {
      alert(`Failed to reset: ${result.error}`);
    }
  };

  // Every invitation the modal creates refreshes the list behind it, so an
  // admin who invites several people sees each one land.
  const handleInvitationCreated = () => {
    fetchInvitations();
  };

  // "Done" (or closing after a create): land on the list that actually shows
  // the new invite — the default 'all' tab can bury it under used/expired
  // ones — and highlight its row.
  const handleCreateDone = (lastInvitationId: string | null) => {
    setShowCreateModal(false);
    setFilterStatus('pending');
    setHighlightedInviteId(lastInvitationId);
    addNotification({ type: 'success', title: 'Invitation created', message: 'It is highlighted in Pending invitations below.', duration: 4000 });
    fetchInvitations();
  };

  // Bring the highlighted row into view once it has rendered, then let the
  // highlight fade after a few seconds.
  useEffect(() => {
    if (!highlightedInviteId) return;
    const row = document.querySelector<HTMLElement>(`[data-invite-id="${highlightedInviteId}"]`);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightedInviteId(null), 6000);
    return () => clearTimeout(t);
  }, [highlightedInviteId, invitations]);

  // ========================================================================
  // Filtering
  // ========================================================================

  const filteredInvitations = invitations.filter(inv => {
    const isExpired = new Date(inv.expiresAt) < new Date();
    const isUsed = !!inv.usedAt;

    switch (filterStatus) {
      case 'pending':
        return !isUsed && !isExpired;
      case 'used':
        return isUsed;
      case 'expired':
        return isExpired && !isUsed;
      default:
        return true;
    }
  });

  // ========================================================================
  // Render Helpers
  // ========================================================================

  const getStatusBadge = (invitation: InvitationWithDetails) => {
    const isExpired = new Date(invitation.expiresAt) < new Date();
    const isUsed = !!invitation.usedAt;

    if (isUsed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
          <Check className="h-3 w-3" />
          Used
        </span>
      );
    }

    if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)]">
          <Clock className="h-3 w-3" />
          Expired
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
        <Mail className="h-3 w-3" />
        Pending
      </span>
    );
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // ========================================================================
  // Main Render
  // ========================================================================

  // For platform admins without a workspace, show workspace selector
  if (!effectiveWorkspaceId) {
    return (
      <div className="p-8">
        <div className="max-w-xl mx-auto text-center">
          <Building2 className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">Select a Workspace</h2>
          <p className="text-[var(--text-ter)] mb-6">
            {isPlatformAdmin 
              ? 'As a platform admin, select which workspace to manage invitations for.'
              : 'Please select a workspace to manage invitations.'}
          </p>
          
          {isPlatformAdmin && workspaces.length > 0 ? (
            <div className="grid gap-3">
              {workspaces.map(ws => (
                <button
                  key={ws.id}
                  onClick={() => setSelectedWorkspaceId(ws.id)}
                  className="w-full p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors text-left flex items-center gap-4"
                >
                  <div className="p-2 bg-[var(--hover-tint)]">
                    <Building2 className="h-5 w-5 text-[var(--text-sec)]" />
                  </div>
                  <div>
                    <p className="font-medium text-[var(--text-pri)]">{ws.name}</p>
                    {ws.description && (
                      <p className="text-sm text-[var(--text-ter)]">{ws.description}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : isPlatformAdmin && workspaces.length === 0 ? (
            <p className="text-[var(--text-ter)]">No workspaces found in this tenant.</p>
          ) : (
            <p className="text-[var(--text-ter)]">Use the tenant switcher in the sidebar to select a workspace.</p>
          )}
        </div>
      </div>
    );
  }

  // Get the effective workspace name for display
  const effectiveWorkspaceName = selectedWorkspaceId 
    ? workspaces.find(w => w.id === selectedWorkspaceId)?.name 
    : currentWorkspace?.name;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<UserPlus className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Invitations"
        subtitle={
          <span className="flex items-center gap-1.5 flex-wrap">
            Manage workspace invitations for
            {isPlatformAdmin && workspaces.length > 1 ? (
              <select
                value={effectiveWorkspaceId || ''}
                onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                className="px-2 py-1 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] hover:border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {workspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>{ws.name}</option>
                ))}
              </select>
            ) : (
              <span className="font-medium text-[var(--text-sec)]">{effectiveWorkspaceName || 'this workspace'}</span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Invite
            </button>
          </div>
        }
      />

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['all', 'pending', 'used', 'expired'] as const).map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2  text-sm font-medium transition-colors ${
              filterStatus === status
                ? 'bg-[var(--bg)] text-white'
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status === 'all' && ` (${invitations.length})`}
            {status === 'pending' && ` (${invitations.filter(i => !i.usedAt && new Date(i.expiresAt) >= new Date()).length})`}
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-[var(--coral-dim)] border border-[var(--coral)] flex items-center gap-3 text-[var(--coral)]">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-[var(--text-ter)] animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredInvitations.length === 0 && (
        <div className="text-center py-12 bg-[var(--surface)] border border-[var(--rule)]">
          <UserPlus className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
            {filterStatus === 'all' ? 'No invitations yet' : `No ${filterStatus} invitations`}
          </h3>
          <p className="text-[var(--text-ter)] mb-4">
            Create an invite link to add team members to this workspace.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create First Invite
          </button>
        </div>
      )}

      {/* Invitations List */}
      {!loading && filteredInvitations.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                  Invite
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                  Roles
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                  Expires
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)]">
              {filteredInvitations.map(invitation => {
                const isPending = !invitation.usedAt && new Date(invitation.expiresAt) >= new Date();
                const isUsed = !!invitation.usedAt;
                
                return (
                  <tr
                    key={invitation.id}
                    data-invite-id={invitation.id}
                    aria-current={invitation.id === highlightedInviteId ? 'true' : undefined}
                    className={
                      invitation.id === highlightedInviteId
                        ? 'bg-[var(--green-dim)] transition-colors'
                        : 'hover:bg-[var(--surface)] transition-colors'
                    }
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[var(--hover-tint)]">
                          <Link2 className="h-4 w-4 text-[var(--text-ter)]" />
                        </div>
                        <div>
                          {invitation.name ? (
                            <p className="font-medium text-[var(--text-pri)]">{invitation.name}</p>
                          ) : invitation.email ? (
                            <p className="font-medium text-[var(--text-pri)]">{invitation.email}</p>
                          ) : (
                            <p className="text-[var(--text-ter)] italic">Open invitation</p>
                          )}
                          {invitation.name && invitation.email && (
                            <p className="text-sm text-[var(--text-ter)]">{invitation.email}</p>
                          )}
                          <p className="text-xs text-[var(--text-ter)] font-mono">
                            {invitation.token.slice(0, 12)}...
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {invitation.roles.map(role => (
                          <span
                            key={role}
                            className="px-2 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs rounded-full"
                          >
                            {role.replace(/-/g, ' ')}
                          </span>
                        ))}
                        {invitation.orgUnitId && (
                          <span className="px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs rounded-full">
                            @ {orgUnits.find(ou => ou.id === invitation.orgUnitId)?.displayName || 'Org Unit'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(invitation)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[var(--text-sec)]">
                        {formatDate(invitation.expiresAt)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {isPending && (
                          <>
                            {invitation.email && (
                              <button
                                onClick={() => handleResendEmail(invitation)}
                                disabled={resendingId === invitation.id}
                                className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50"
                                title={`Resend email to ${invitation.email}`}
                                aria-label={`Resend email to ${invitation.email}`}
                              >
                                {resendingId === invitation.id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Mail className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleCopyLink(invitation)}
                              className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                              title="Copy invite link"
                            >
                              {copiedId === invitation.id ? (
                                <Check className="h-4 w-4 text-[var(--green)]" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                            <ReverseActionButton
                              label="Revoke"
                              confirmTitle="Revoke invitation"
                              explain={`This will expire the invitation for ${
                                invitation.name || invitation.email || 'this open invitation'
                              }. Anyone who has not yet used it will no longer be able to join. The invitation stays in the list as Expired.`}
                              run={(reason) => revokeWorkspaceInvitation(invitation.id, reason)}
                              onDone={fetchInvitations}
                              variant="ghost"
                              size="sm"
                              className="!p-2 !text-[var(--text-ter)] hover:!text-[var(--coral)] hover:!bg-[var(--coral-dim)]"
                              title="Revoke invitation"
                            />
                          </>
                        )}
                        {isUsed && (
                          <button
                            onClick={() => handleReset(invitation.id)}
                            className="p-2 text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
                            title="Reset invitation (allow retry)"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Invite Modal */}
      {showCreateModal && effectiveWorkspaceId && currentTenantId && (
        <CreateInviteModal
          workspaceId={effectiveWorkspaceId}
          tenantId={currentTenantId}
          invitedBy={userId}
          assignableRoles={assignableRoles}
          orgUnits={orgUnits}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleInvitationCreated}
          onDone={handleCreateDone}
        />
      )}
    </div>
  );
}

// ============================================================================
// Create Invite Modal
// ============================================================================

interface CreateInviteModalProps {
  workspaceId: string;
  tenantId: string;
  invitedBy: string;
  assignableRoles: AssignableRole[];
  orgUnits: OrgUnit[];
  onClose: () => void;
  /** Fired after each successful create, so the list behind the modal refreshes. */
  onCreated: () => void;
  /** Close after at least one create; receives the last invitation's id to highlight. */
  onDone: (lastInvitationId: string | null) => void;
}

function CreateInviteModal({ workspaceId, tenantId, invitedBy, assignableRoles, orgUnits, onClose, onCreated, onDone }: CreateInviteModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [lastCreatedId, setLastCreatedId] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState(0);
  /** Emailing the invitation just created: idle (no address), sending, or the outcome. */
  const [emailState, setEmailState] = useState<
    | { status: 'idle' }
    | { status: 'sending'; to: string }
    | { status: 'done'; to: string; sent: boolean; message: string }
  >({ status: 'idle' });

  // Start a fresh invitation: every field back to its default.
  const resetForAnother = () => {
    setCreatedLink(null);
    setCopied(false);
    setError(null);
    setEmailState({ status: 'idle' });
    setName('');
    setEmail('');
    setSelectedRoles([]);
    setSelectedOrgUnitId('');
    setExpiresInDays(7);
  };

  // Closing after a create is "Done", not "Cancel" — the invite exists.
  const handleClose = () => {
    if (lastCreatedId) onDone(lastCreatedId);
    else onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    const params: CreateInvitationParams = {
      workspaceId,
      tenantId,
      invitedBy,
      name: name || undefined,
      email: email || undefined,
      roles: selectedRoles.length ? selectedRoles : undefined,
      orgUnitId: selectedOrgUnitId || undefined,
      expiresInDays,
    };

    const result = await createInvitation(params);

    if (result.error || !result.invitation) {
      setError(result.error || 'Failed to create invitation');
      setCreating(false);
      return;
    }

    const link = getInviteUrl(result.invitation.token);
    setCreatedLink(link);
    setLastCreatedId(result.invitation.id);
    setCreatedCount((n) => n + 1);
    setCreating(false);
    onCreated();

    // Email it when an address was given. Best effort: the invitation exists
    // either way, and the link stays on screen if the email does not go out.
    const to = result.invitation.email;
    if (to) {
      setEmailState({ status: 'sending', to });
      const sent = await sendInvitationEmail(result.invitation.id);
      setEmailState({ status: 'done', to, ...inviteEmailFeedback(to, sent) });
    } else {
      setEmailState({ status: 'idle' });
    }
  };

  const emailed = emailState.status === 'done' && emailState.sent;

  const handleCopyLink = async () => {
    if (!createdLink) return;
    await navigator.clipboard.writeText(createdLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleRole = (roleName: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--coral-dim)]">
              <UserPlus className="h-5 w-5 text-[var(--coral)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)]">Create Invitation</h2>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        {createdLink ? (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="p-3 bg-[var(--green-dim)] rounded-full w-fit mx-auto mb-4">
                <Check className="h-8 w-8 text-[var(--green)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">Invitation created</h3>
              <p className="text-[var(--text-ter)]">
                {emailed
                  ? 'We emailed them a link to join. You can also copy it below.'
                  : 'Share this link with the person you want to invite.'}
              </p>
            </div>

            {emailState.status === 'sending' && (
              <Callout tone="slate" className="mb-6">
                <span className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Emailing the invitation to {emailState.to}…
                </span>
              </Callout>
            )}
            {emailState.status === 'done' && (
              <Callout tone={emailState.sent ? 'green' : 'mustard'} className="mb-6">
                <span className="flex items-center gap-2">
                  {emailState.sent ? <Mail className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {emailState.message}
                </span>
              </Callout>
            )}

            <div className="bg-[var(--surface)] p-4 mb-6">
              <p className="text-xs text-[var(--text-ter)] mb-2">Invite link</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={createdLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-[var(--bg-raised)] border border-[var(--rule)] text-sm font-mono"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2  font-medium transition-colors ${
                    copied
                      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      : 'bg-[var(--coral)] text-white hover:bg-[var(--coral)]'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <Callout tone="slate" label="Next steps" className="mb-6">
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  {emailed
                    ? 'They get an email with the link. You can also copy it and send it another way.'
                    : 'Copy the link and send it to them — by email, chat, however you usually reach them.'}
                </li>
                <li>They open it, sign in or create an account, and join this workspace with the roles you picked.</li>
                <li>
                  Until then it waits in <strong>Pending invitations</strong>, where you can copy the link again,
                  resend the email, or revoke it.
                  {createdCount > 1 && ` You have created ${createdCount} invitations in this window.`}
                </li>
              </ol>
            </Callout>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={resetForAnother}
                className="flex-1 px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
              >
                Invite another
              </button>
              <button
                type="button"
                onClick={() => onDone(lastCreatedId)}
                className="flex-1 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
              >
                Done — show it in the list
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              />
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                The invitee's name (used for their account)
              </p>
            </div>

            {/* Email (Optional) */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Email address (optional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              />
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                We'll email them the invite link. Leave empty to allow anyone with the link to sign up.
              </p>
            </div>

            {/* Org Unit (WHERE) */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Where (Org Unit Scope)
              </label>
              <select
                value={selectedOrgUnitId}
                onChange={(e) => setSelectedOrgUnitId(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              >
                <option value="">Global (entire workspace)</option>
                {orgUnits.map(ou => (
                  <option key={ou.id} value={ou.id}>
                    {'—'.repeat(ou.depth)} {ou.displayName}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Roles will apply to this org unit and its children
              </p>
            </div>

            {/* Roles (WHAT) — only roles that exist in the database */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                What (Roles)
              </label>
              {assignableRoles.length === 0 ? (
                <p className="text-sm text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2">
                  No roles found for this workspace. Create roles on <strong>Roles & Permissions</strong> first, then create an invitation.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {assignableRoles.map(role => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => toggleRole(role.name)}
                      className={`px-3 py-2  border text-sm text-left transition-colors ${
                        selectedRoles.includes(role.name)
                          ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                          : 'border-[var(--rule)] hover:border-[var(--rule)]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {selectedRoles.includes(role.name) ? (
                          <Check className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <Shield className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                        )}
                        <div>
                          <span className="font-medium">{role.label}</span>
                          {role.description && (
                            <span className="text-[var(--text-ter)] ml-2 text-xs">{role.description}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-[var(--text-ter)] mt-2">
                Select the roles this user should have (from Roles & Permissions)
              </p>
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Expires In
              </label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            {/* Error */}
            {error && (
              <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)] flex items-center gap-3 text-[var(--coral)]">
                <AlertCircle className="h-5 w-5" />
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex-1 px-4 py-3 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Create Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
